import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@/components/ui/icons';
import TeeSectionHeader from '@/components/ui/tee-section-header';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeTable, Column } from '@/components/ui/tee-table';
import { TeeActionsMenu } from '@/components/ui/tee-actions-menu';
import { TeeConfirmationDialog } from '@/components/ui/tee-confirmation-dialog';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeDropDownList } from '@/components/ui/tee-dropdown-list';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeTabs, TeeTabsList, TeeTabsTrigger, TeeTabsContent } from '@/components/ui/tee-tabs';
import { TeeContentThumbnail } from '@/components/ui/tee-content-thumbnail';
import { useContentItems } from '@/hooks/useContentItems';
import { useTaxonomy } from '@/hooks/useTaxonomy';
import { TaxonomyType } from '@/types/taxonomy';
import {
  ContentItem,
  ContentMediaType,
  ContentStatus,
  VideoProcessingStatus,
} from '@/types/contentItem';
import { Pagination } from '@/types/pagination';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import { getErrorMessage } from '@/utils/error';
import ContentItemFormModal from './ContentItemFormModal';
import ContentItemPreviewModal from './ContentItemPreviewModal';
import PlaylistsPanel from './PlaylistsPanel';
import { MEDIA_TYPE_LABEL_KEY } from '@/utils/contentMediaType';

// Exhaustive mapping so a future VideoProcessingStatus enum value is a
// compile error rather than a silently blank badge. Raw enum strings never
// appear as UI copy (T-314-31/UI-SPEC copywriting contract) — every value
// resolves through an i18n key.
const VIDEO_PROCESSING_BADGE: Record<
  VideoProcessingStatus,
  { type: 'grey' | 'green' | 'red'; labelKey: string }
> = {
  [VideoProcessingStatus.PENDING]: { type: 'grey', labelKey: 'contentLibrary.videoStatusUploading' },
  [VideoProcessingStatus.UPLOADING]: { type: 'grey', labelKey: 'contentLibrary.videoStatusUploading' },
  [VideoProcessingStatus.PROCESSING]: { type: 'grey', labelKey: 'contentLibrary.videoStatusProcessing' },
  [VideoProcessingStatus.READY]: { type: 'green', labelKey: 'contentLibrary.videoStatusReady' },
  [VideoProcessingStatus.FAILED]: { type: 'red', labelKey: 'contentLibrary.videoStatusFailed' },
};

export default function ContentStudioPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'content';
  const setActiveTab = (tab: string) => setSearchParams({ tab });

  const { options: taxonomyOptions } = useTaxonomy();
  const categoryLabelById = Object.fromEntries(
    taxonomyOptions[TaxonomyType.CATEGORY].map((o) => [o.value, o.label])
  );

  const {
    items,
    loading,
    error,
    total,
    offset,
    setOffset,
    limit,
    filters,
    setFilters,
    listContentItems,
    deleteContentItem,
    updateStatus,
  } = useContentItems();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ContentItem | null>(null);
  const [previewTarget, setPreviewTarget] = useState<ContentItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ContentItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false);

  const [search, setSearch] = useState('');

  useEffect(() => {
    listContentItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced title/tag search (300ms) — dropdown filters below fire
  // immediately since they are discrete, low-frequency events.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextFilters = { ...filters, search: search || undefined };
      setFilters(nextFilters);
      setOffset(0);
      listContentItems({ offset: 0, filters: nextFilters });
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const statusOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    { label: t('contentLibrary.statusDraft'), value: ContentStatus.DRAFT },
    { label: t('contentLibrary.statusPublished'), value: ContentStatus.PUBLISHED },
  ];

  const mediaTypeOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...Object.values(ContentMediaType).map((value) => ({
      label: t(MEDIA_TYPE_LABEL_KEY[value]),
      value,
    })),
  ];

  const categoryOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...taxonomyOptions[TaxonomyType.CATEGORY],
  ];

  const isFilterActive = Boolean(
    filters.status || filters.mediaType || filters.categoryId || filters.search
  );

  const handleStatusFilterChange = (value: string | number) => {
    const next = { ...filters, status: value ? (value as ContentStatus) : undefined };
    setFilters(next);
    setOffset(0);
    listContentItems({ offset: 0, filters: next });
  };

  const handleMediaTypeFilterChange = (value: string | number) => {
    const next = { ...filters, mediaType: value ? (value as ContentMediaType) : undefined };
    setFilters(next);
    setOffset(0);
    listContentItems({ offset: 0, filters: next });
  };

  const handleCategoryFilterChange = (value: string | number) => {
    const next = { ...filters, categoryId: value ? String(value) : undefined };
    setFilters(next);
    setOffset(0);
    listContentItems({ offset: 0, filters: next });
  };

  const handleClearFilters = () => {
    setSearch('');
    setFilters({});
    setOffset(0);
    listContentItems({ offset: 0, filters: {} });
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pagination: Pagination = { page: currentPage, pageSize: limit, total, totalPages };

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
    listContentItems({ offset: newOffset });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteContentItem(deleteTarget.id);
      showSuccessToast(t('contentLibrary.itemDeleted'));
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      showErrorToast(getErrorMessage(error, t('contentLibrary.errorDeleteItem')));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (row: ContentItem) => {
    try {
      await updateStatus(
        row.id,
        row.status === ContentStatus.PUBLISHED ? ContentStatus.DRAFT : ContentStatus.PUBLISHED
      );
    } catch {
      showErrorToast(t('contentLibrary.errorStatusUpdate'));
    }
  };

  const columns: Column<ContentItem>[] = [
    {
      key: 'imageUrl',
      label: t('contentLibrary.colThumbnail'),
      render: (_v, row) => {
        const thumbnailUrl = row.videoThumbnailUrl ?? row.imageSignedUrl ?? row.imageUrl;
        return (
          <button
            type="button"
            onClick={() => setPreviewTarget(row)}
            aria-label={t('contentLibrary.ariaPreview', { title: row.title })}
            className="group shrink-0"
          >
            <TeeContentThumbnail
              title={row.title}
              mediaType={row.mediaType}
              thumbnailUrl={thumbnailUrl}
              className="h-10 w-12"
              iconSize={20}
              previewable
            />
          </button>
        );
      },
    },
    {
      key: 'title',
      label: t('contentLibrary.colTitle'),
      render: (_v, row) => (
        <span className="truncate text-body-m font-normal text-[var(--text-content-title)]">
          {row.title}
        </span>
      ),
    },
    {
      key: 'mediaType',
      label: t('contentLibrary.colMediaType'),
      render: (_v, row) => (
        <TeeBadge type="grey" label={t(MEDIA_TYPE_LABEL_KEY[row.mediaType])} />
      ),
    },
    {
      key: 'categoryIds',
      label: t('contentLibrary.colCategories'),
      render: (_v, row) => {
        if (!row.categoryIds || row.categoryIds.length === 0) return '-';
        const labels = row.categoryIds.map((id) => categoryLabelById[id] ?? id);
        const [first, ...rest] = labels;
        return rest.length > 0 ? `${first} +${rest.length}` : first;
      },
    },
    {
      key: 'status',
      label: t('contentLibrary.colStatus'),
      render: (_v, row) => {
        const processing =
          row.mediaType === ContentMediaType.VIDEO && row.videoProcessingStatus
            ? VIDEO_PROCESSING_BADGE[row.videoProcessingStatus]
            : null;
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {row.status === ContentStatus.PUBLISHED ? (
              <TeeBadge type="green" label={t('contentLibrary.statusPublished')} />
            ) : (
              <TeeBadge type="grey" label={t('contentLibrary.statusDraft')} />
            )}
            {processing && <TeeBadge type={processing.type} label={t(processing.labelKey)} />}
          </div>
        );
      },
    },
    {
      key: 'updatedAt',
      label: t('contentLibrary.colUpdated'),
      render: (_v, row) => new Date(row.updatedAt).toLocaleDateString(),
    },
    {
      key: 'id',
      label: '',
      render: (_v, row) => (
        <TeeActionsMenu
          onView={() => setPreviewTarget(row)}
          onEdit={() => {
            setEditTarget(row);
            setShowFormModal(true);
          }}
          onDelete={() => {
            setDeleteTarget(row);
            setDeleteConfirmOpen(true);
          }}
          onToggleStatus={() => handleToggleStatus(row)}
          status={row.status === ContentStatus.PUBLISHED ? 'active' : 'inactive'}
        />
      ),
    },
  ];

  return (
    <div className="section_container">
      <TeeSectionHeader
        title={t('contentLibrary.title')}
        actionLabel={
          activeTab === 'playlists'
            ? t('contentLibrary.createPlaylist')
            : t('contentLibrary.addContentItem')
        }
        onAction={() => {
          if (activeTab === 'playlists') {
            setCreatePlaylistOpen(true);
          } else {
            setEditTarget(null);
            setShowFormModal(true);
          }
        }}
      />

      <TeeTabs value={activeTab} onChange={setActiveTab} className="w-full">
        <TeeTabsList>
          <TeeTabsTrigger value="content">{t('contentLibrary.tabContent')}</TeeTabsTrigger>
          <TeeTabsTrigger value="playlists">{t('contentLibrary.tabPlaylists')}</TeeTabsTrigger>
        </TeeTabsList>

        <TeeTabsContent value="content">
          <div className="section_body">
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-[240px]">
                <TeeInput
                  type="text"
                  placeholder={t('contentLibrary.searchPlaceholder')}
                  leadingIcon={<IconSearch className="w-4 h-4" />}
                  inputSize="s"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="w-[160px]">
                <TeeDropDownList
                  options={statusOptions}
                  value={filters.status ?? ''}
                  onChange={handleStatusFilterChange}
                  label={t('contentLibrary.filterStatus')}
                />
              </div>
              <div className="w-[180px]">
                <TeeDropDownList
                  options={mediaTypeOptions}
                  value={filters.mediaType ?? ''}
                  onChange={handleMediaTypeFilterChange}
                  label={t('contentLibrary.filterMediaType')}
                />
              </div>
              <div className="w-[180px]">
                <TeeDropDownList
                  options={categoryOptions}
                  value={filters.categoryId ?? ''}
                  onChange={handleCategoryFilterChange}
                  label={t('contentLibrary.filterCategory')}
                />
              </div>
              {isFilterActive && (
                <TeeButton
                  variant="ghost"
                  size="s"
                  text={t('contentLibrary.clearFilters')}
                  onClick={handleClearFilters}
                />
              )}
            </div>

            {loading ? (
              <TeeLoader />
            ) : error ? (
              <div className="flex flex-col items-center gap-4 py-12">
                <p className="text-body-m text-[var(--text-content-sub-title)]">
                  {t('contentLibrary.errorLoadItems')}
                </p>
                <TeeButton
                  variant="secondary"
                  size="s"
                  text={t('contentLibrary.retry')}
                  onClick={() => listContentItems()}
                />
              </div>
            ) : items.length === 0 && !isFilterActive ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <p className="text-heading-h4 font-semibold text-[var(--text-content-title)]">
                  {t('contentLibrary.emptyContentHeading')}
                </p>
                <p className="text-body-m text-[var(--text-content-sub-title)]">
                  {t('contentLibrary.emptyContentBody')}
                </p>
                <TeeButton
                  variant="primary"
                  size="s"
                  text={t('contentLibrary.addContentItem')}
                  onClick={() => {
                    setEditTarget(null);
                    setShowFormModal(true);
                  }}
                />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-4 py-12 text-center">
                <p className="text-body-m text-[var(--text-content-sub-title)]">
                  {t('contentLibrary.emptyFiltered')}
                </p>
                <TeeButton
                  variant="secondary"
                  size="s"
                  text={t('contentLibrary.clearFilters')}
                  onClick={handleClearFilters}
                />
              </div>
            ) : (
              <TeeTable
                columns={columns}
                data={items}
                pagination={total > 0 ? pagination : undefined}
                onPageChange={handlePageChange}
                rowKey={(row) => row.id}
              />
            )}
          </div>
        </TeeTabsContent>

        <TeeTabsContent value="playlists">
          <PlaylistsPanel
            createModalOpen={createPlaylistOpen}
            onCloseCreateModal={() => setCreatePlaylistOpen(false)}
          />
        </TeeTabsContent>
      </TeeTabs>

      <ContentItemFormModal
        isOpen={showFormModal}
        editTarget={editTarget}
        onClose={() => {
          setShowFormModal(false);
          setEditTarget(null);
        }}
        onSaved={() => listContentItems()}
      />

      <TeeConfirmationDialog
        isOpen={deleteConfirmOpen}
        title={t('contentLibrary.deleteItemTitle')}
        description={t('contentLibrary.deleteItemBody', { title: deleteTarget?.title ?? '' })}
        variant="destructive"
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        isLoading={isDeleting}
      />

      <ContentItemPreviewModal
        isOpen={previewTarget !== null}
        item={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </div>
  );
}
